import React, { useEffect, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { displayTooltip } from '~/hooks/displayTooltip';

// Actions
import { success } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { dateFormat } from '~/utils/date-format';
import { copyToClipboard } from '~/utils/helperFunctions';
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';

// Componets
import OlcTablePagination from '~/components/General/OlcTablePagination';
import TruncatedText from '~/components/General/TruncatedText';
import OlcActionMenu from '~/components/General/OlcActionMenu';

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import { Box, Typography, Tooltip, Stack, IconButton } from '@mui/material';

// Icons
import {
  HideCIcon,
  ShowCIcon,
  CopyCIcon,
  RotateCIcon,
  DeleteSettingCIcon,
  Edit4CIcon,
  NoContactCIcon,
  InfoCIcon,
} from '~/assets/images';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// Styles
import './styles.scss';

const index = (props) => {
  const {
    setDeleteApiModal,
    setRotateApiModal,
    setSelectedApi,
    updatePagination,
    setEditApiModal,
    setSelectedApiKeyData,
    pagination,
    initialLoad,
  } = props;
  const [showApiKey, setShowApiKey] = useState([]);
  const rows = useSelector((state) => state.apiKeysReducers.apiKeys);
  const rowsCount = useSelector((state) => state.apiKeysReducers.rowsCount);
  const to = useSelector((state) => state.apiKeysReducers.to);
  const from = useSelector((state) => state.apiKeysReducers.from);
  const lastPage = useSelector((state) => state.apiKeysReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.apiKeysReducers.currentPage || 1);
  const isLoading = useSelector((state) => state.apiKeysReducers.loading);
  const perPage = useSelector((state) => state.apiKeysReducers.perPage) || pagination.pageSize;

  const dispatch = useDispatch();
  const pageSizes = DEFAULT_PAGE_SIZES;
  const paginationPageSizes = pageSizes.includes(perPage)
    ? pageSizes
    : [perPage, ...pageSizes].filter(Boolean);

  const toggleApiKeyVisibility = (id, event) => {
    event?.stopPropagation();
    setShowApiKey((visibleKeys) =>
      visibleKeys.includes(id)
        ? visibleKeys.filter((visibleKeyId) => visibleKeyId !== id)
        : [...visibleKeys, id]
    );
  };

  const handleDelete = (row) => {
    setDeleteApiModal(row.id);
  };

  const handleEdit = (data) => {
    setSelectedApiKeyData(data);
    setSelectedApi(data.id);
    setEditApiModal(true);
  };

  const handleRotate = (id) => {
    setSelectedApi(id);
    setRotateApiModal(true);
  };

  const handleChangePage = (event, newPage) => {
    setPaginationLoading(true);
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const copyKey = (key) => {
    copyToClipboard(key);
    dispatch(success(MESSAGES.SETTINGS.API_KEYS.COPY_KEY));
  };

  const isTooltip = displayTooltip();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [activeRowId, setActiveRowId] = useState({});
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [showNoRows, setShowNoRows] = useState(false);
  const showLoader = initialLoad || isLoading || paginationLoading;

  useEffect(() => {
    setPaginationLoading(false);
  }, [rows]);

  const shopifyStoreName = (row) => {
    if (row.source === 'Shopify' && row.meta?.shop) {
      return (
        <Typography className="shopifyStoreName">
          <span>Store Name:</span> {row.meta?.shop}
        </Typography>
      );
    }
    return '-';
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      width: 190,
      compactGrow: 3,
      compactMinWidth: 240,
      compactMaxWidth: 300,
      compactShrinkMinWidth: 180,
      compactShrinkPriority: 1,
      sortable: false,
      flex: window.innerWidth >= 1500 ? 1 : 0,
      cellClassName: 'apiKeyNameCell',
      renderCell: (params) => (
        <Box className="addressWrapper apiKeyNameWrapper">
          <TruncatedText text={params.row.name} />
          {params.row.source === 'Shopify' && shopifyStoreName(params.row) ? (
            <Tooltip title={shopifyStoreName(params.row) ? shopifyStoreName(params.row) : '-'}>
              <img src={InfoCIcon} alt="Shopify store information" />
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
    {
      field: 'key',
      headerName: 'Key',
      width: 200,
      minWidth: 200,
      compactFill: false,
      compactGrow: 3,
      compactMinWidth: 200,
      compactMaxWidth: 420,
      compactShrinkMinWidth: 140,
      compactShrinkPriority: 0,
      height: 500,
      sortable: false,
      renderCell: (params) => (
        <Box className="secretKey apiKeySecretKey">
          <Tooltip
            title={showApiKey.includes(params.row.id) ? params?.row?.key : ''}
            disableHoverListener={isTooltip}
          >
            <input
              type={showApiKey.includes(params?.row?.id) ? 'text' : 'password'}
              value={
                showApiKey.includes(params?.row?.id)
                  ? params?.row?.key
                  : params?.row?.key?.slice(0, 13)
              }
              readOnly
            />
          </Tooltip>
          <Box className="hideShow">
            {showApiKey.includes(params.row.id) ? (
              <img
                src={HideCIcon}
                alt=""
                role="button"
                tabIndex={0}
                aria-label={`Hide API key ${params.row.name}`}
                onClick={(event) => toggleApiKeyVisibility(params.row.id, event)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleApiKeyVisibility(params.row.id, event);
                  }
                }}
              />
            ) : (
              <img
                src={ShowCIcon}
                alt=""
                role="button"
                tabIndex={0}
                aria-label={`Show API key ${params.row.name}`}
                onClick={(event) => toggleApiKeyVisibility(params.row.id, event)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleApiKeyVisibility(params.row.id, event);
                  }
                }}
              />
            )}
            <img
              src={CopyCIcon}
              alt=""
              role="button"
              tabIndex={0}
              aria-label={`Copy API key ${params.row.name}`}
              onClick={() => copyKey(params?.row?.key)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  copyKey(params?.row?.key);
                }
              }}
            />
          </Box>
        </Box>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 120,
      compactFill: false,
      compactMinWidth: 100,
      compactMaxWidth: 160,
      compactShrinkMinWidth: 90,
      compactShrinkPriority: 1,
      sortable: false,
      flex: window.innerWidth >= 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.joinedSource ? params.row.joinedSource : '-'}</Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      width: 220,
      compactMinWidth: 150,
      compactMaxWidth: 180,
      compactShrinkMinWidth: 135,
      compactShrinkPriority: 1,
      sortable: false,
      flex: window.innerWidth >= 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{dateFormat(params.row.createdAt)}</Typography>
        </Box>
      ),
    },
    {
      field: 'expiredAt',
      headerName: 'Expiry Date',
      width: 220,
      compactMinWidth: 150,
      compactMaxWidth: 180,
      compactShrinkMinWidth: 135,
      compactShrinkPriority: 1,
      sortable: false,
      flex: window.innerWidth >= 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>
            {params.row.expiredAt === null
              ? MESSAGES.SETTINGS.API_KEYS.NO_EXPIRY
              : dateFormat(params.row.expiredAt)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'lastUsedAt',
      headerName: 'Last Used Date',
      width: 220,
      compactMinWidth: 150,
      compactMaxWidth: 180,
      compactShrinkMinWidth: 135,
      compactShrinkPriority: 1,
      sortable: false,
      flex: window.innerWidth >= 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>
            {params.row.lastUsedAt === null ? ' -' : dateFormat(params.row.lastUsedAt)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 70,
      sortable: false,
      renderCell: (params) => {
        const isActive = open && activeRowId?.id === params.row.id;

        return (
          <IconButton
            aria-label={`Open actions for API key ${params.row.name}`}
            id={`api-key-actions-${params.row.id}`}
            aria-controls={isActive ? 'api-key-actions-menu' : undefined}
            aria-expanded={isActive ? 'true' : undefined}
            aria-haspopup="menu"
            onClick={(event) => {
              setActiveRowId(params.row);
              handleClick(event);
            }}
            disableRipple
          >
            <MoreVertIcon
              sx={{
                opacity: isActive ? '0.8 !important' : '0.4 !important',
                '&:hover': {
                  opacity: '0.8 !important',
                },
              }}
            />
          </IconButton>
        );
      },
    },
  ];

  useEffect(() => {
    if (rows.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [rows]);

  // Delayed "No Results" overlay, matching other tables
  useEffect(() => {
    let timeoutId;

    if (!(initialLoad || isLoading || paginationLoading) && rows.length === 0) {
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300);
    } else {
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [initialLoad, isLoading, paginationLoading, rows.length]);

  return (
    <Box
      className={`olcTableGridInternal ${
        initialLoad || isLoading || paginationLoading ? 'table-loading' : ''
      } ${rows.length === 0 ? 'table-empty' : ''}`}
    >
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={initialLoad || isLoading || paginationLoading}
          rowCount={rowsCount}
          paginationMode="server"
          hideFooterSelectedRowCount
          hideFooterPagination
          rowSelection={false}
          getRowId={(row) => row?.id}
          disableColumnMenu={true}
          rowHeight={61}
          shrinkColumnsToFit
          sx={{
            ...(showNoRows ||
            (!(initialLoad || isLoading || paginationLoading) && rows.length === 0)
              ? {
                  height: 'auto',
                  minHeight: '200px',
                }
              : {}),
          }}
          slots={{
            noRowsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsTextApi">No Results Found</Typography>
              ) : null,
            noResultsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsTextApi">No Results Found</Typography>
              ) : null,
          }}
        />
      </Box>

      <OlcActionMenu
        id="api-key-actions-menu"
        labelledBy={activeRowId?.id !== undefined ? `api-key-actions-${activeRowId.id}` : undefined}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        actions={[
          {
            label: 'Edit',
            icon: Edit4CIcon,
            onClick: () => handleEdit(activeRowId),
          },
          {
            label: 'Rotate',
            icon: RotateCIcon,
            onClick: () => handleRotate(activeRowId.id),
          },
          {
            label: 'Delete',
            icon: DeleteSettingCIcon,
            onClick: () => handleDelete(activeRowId),
          },
        ]}
      />

      {(showLoader || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage}
          pageSizes={paginationPageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={(event) => {
            setPaginationLoading(true);
            updatePagination({ page: 1, pageSize: event.target.value });
          }}
          from={from}
          to={rowsCount < to ? rows.length + from - 1 : to}
          total={rowsCount}
          loading={showLoader}
        />
      )}
    </Box>
  );
};

export default index;
